import React, { useState, useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { PanelCards } from '../../../Panel';
import ApplicationPreview from '../../../../components/cards/applications/ApplicationPreview';
import ApplicationsDetails from '../ApplicationsDetails';
import { ApplicationsData } from '../../../../types/Application';
import { trackEvent } from '../../../../actions/analyticsActions';
import { IN_SEASON } from '../../../../constants/mapLayerIds';
import { FHA, PRESCRIPTIONS, WEATHER } from '../../../../constants/cards';
import { useAppSelector } from '../../../../utils';
import { getApplicationTreatmentsBySeason } from '../../../../selectors/applicationTreatmentSelectors';
import { userSelector } from '../../../../selectors/userSelectors';
import { treatmentsToApplicationsData } from '../../../../utils/applicationTreatmentTransformers';

const CARDS = [WEATHER, PRESCRIPTIONS, FHA];
const LAYER = IN_SEASON.APPLICATIONS;

interface ActiveApplication {
  crop: string;
  endDate: number;
}

interface ApplicationsPanelV2Props {
  activeApplication: ActiveApplication;
  collapsePanel: () => void;
  index: number;
  isCollapsed: boolean;
  isSplitViewEnabled: boolean;
  locale: string;
  season: string;
  setActiveSubLayer: (layer: string, activeSubLayerId: string, index: number, season: string) => void;
}

export const ApplicationsPanelV2: React.FC<ApplicationsPanelV2Props> = ({
  activeApplication,
  collapsePanel,
  index,
  isCollapsed,
  isSplitViewEnabled,
  locale,
  season,
  setActiveSubLayer: setActiveSubLayerProp,
}) => {
  const dispatch = useDispatch();
  const [expandedApplications, setExpandedApplications] = useState<Set<string>>(new Set());
  
  const state = useAppSelector(state => state);
  const user = userSelector(state);
  const fieldUUID = state.field?.uuid?.toUpperCase() ?? '';
  
  const activeSeason = state.seasons.activeSeasons[index];
  const currentSeasonId = activeSeason?.id ?? season;
  
  const treatments = useMemo(
    () => getApplicationTreatmentsBySeason(state, currentSeasonId),
    [state, currentSeasonId]
  );

  const applicationsData: ApplicationsData = useMemo(() => (
    treatmentsToApplicationsData(treatments, {
      userUuid: user?.uuid ?? '',
      fieldUUID,
      locale,
    })
  ), [treatments, user?.uuid, fieldUUID, locale]);

  const handleToggleApplicationExpand = useCallback((applicationKey: string) => {
    const newExpandedApplications = new Set(expandedApplications);
    
    if (newExpandedApplications.has(applicationKey)) {
      newExpandedApplications.delete(applicationKey);
    } else {
      newExpandedApplications.add(applicationKey);
    }
    
    setExpandedApplications(newExpandedApplications);

    dispatch(trackEvent('DMB - Individual Application Expanded', {
      applicationKey,
      expanded: !expandedApplications.has(applicationKey)
    }));
  }, [expandedApplications, dispatch]);

  const handleApplicationSelect = useCallback((applicationName: string, endDate: string) => {
    const endMs = Number(endDate);
    const encodedCrop = encodeURIComponent(encodeURIComponent(applicationName));
    const activeSubLayerId = `${endMs}_${encodedCrop}`;

    setActiveSubLayerProp(LAYER, activeSubLayerId, index, season);
    collapsePanel();
  }, [setActiveSubLayerProp, index, season, collapsePanel]);

  const getDate = useCallback((date: number) => {
    const dateOpts: Intl.DateTimeFormatOptions = {
      weekday: 'short',
      month: '2-digit',
      year: 'numeric',
      day: '2-digit',
    };
    return new Date(date).toLocaleDateString(locale, dateOpts);
  }, [locale]);

  const renderFirstCard = useCallback(() => {
    if (isSplitViewEnabled && isCollapsed) {
      return (
        <ApplicationPreview
          activeApplicationName={activeApplication ? 'Application' : undefined}
          decrementActiveApplication={() => undefined}
          incrementActiveApplication={() => undefined}
        />
      );
    }

    return (
      <ApplicationsDetails
        applicationsData={applicationsData}
        testId="applications-panel-v2"
        expandedApplications={expandedApplications}
        onToggleApplicationExpand={handleToggleApplicationExpand}
        onApplicationSelect={handleApplicationSelect}
        season={season}
        locale={locale}
      />
    );
  }, [
    isSplitViewEnabled,
    isCollapsed,
    activeApplication,
    applicationsData,
    expandedApplications,
    handleToggleApplicationExpand,
    handleApplicationSelect,
    season,
    locale
  ]);

  const activeApplicationDate = activeApplication ? getDate(activeApplication.endDate) : undefined;

  return (
    <PanelCards
      cards={CARDS}
      firstCard={renderFirstCard()}
      headerDetail={activeApplicationDate}
      index={index}
      isCollapsed={isCollapsed}
      testId="application-v2"
    />
  );
};

export default ApplicationsPanelV2;
